import { HaftpflichtSchadenSpritzschaden } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_SELECT, MATCH_VISIBLE } from '@ng-dynamic-forms/core';
import { MATCH_COMPARE_WITH_HIDDEN } from '../../matchers/compare-with-visible.matcher';
import { TYPE_CUSTOM_FILE_UPLOAD } from '../custom-file-upload/custom-file-upload.model';
import { TYPE_DOKUMENTEN_ANGABE } from '../dokumenten-angabe/dokumenten-angabe.model';
import { TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON } from '../haftpflicht-geschaedigte-person/haftpflicht-geschaedigte-person.model';
import { TYPE_HAFTPFLICHT_SPRITZ_WALDARBEITEN_AUFSTELLUNG } from '../haftpflicht-spritz-waldarbeiten-aufstellung/haftpflicht-spritz-waldarbeiten-aufstellung.model';
import { TYPE_INFO } from '../info/info.model';
import { AnyFormControlModel } from '../shared-models/any-form-control.model';
import { AufstellungVorhanden, HaftpflichtSchadenVorhanden } from '../shared-models/haftpflichtschaden.model';
import { TYPE_ZAHLUNGSEMPFAENGER } from '../zahlungsempfaenger/zahlungsempfaenger.model';
import { HaftpflichtWaldarbeitenAdditional } from './haftpflicht-sachschaden-waldarbeiten.model';

const GESCHAEDIGTE_PERSON_RELATION = {
  match: MATCH_COMPARE_WITH_HIDDEN,
  when:  [
    {
      id:          'geschaedigtePerson',
      compareWith: val => !val?.vorhanden || val.vorhanden === HaftpflichtSchadenVorhanden.Nachgebracht
    }
  ]
};

export function HAFTPFLICHT_WALDARBEITEN_TEMPLATE(
  value: HaftpflichtSchadenSpritzschaden,
  modelValue,
  {
    docTypBelege,
    geschPersList,
    kundeList,
    updateClaim,
    paperProcessOverride,
    spritzschaden
  }: HaftpflichtWaldarbeitenAdditional = {},
  isEditEvent = false
): AnyFormControlModel[] {
  const {
    id,
    vorhanden,
    geschaedigtePerson,
    aufstellungVorhanden,
    aufstellung: {
                   checklistItemFileList: aufstellungFileList,
                   zahlungsempfaenger:    aufstellungZahlungsempfaenger,
                   generationData:        {spritzschadenAufstellung} = {} as any
                 } = {} as any,
    belege,
    bioKultur
  } = value || {};

  if (!isEditEvent && geschPersList && modelValue.length) {
    // only persons who are not in the local modelValue
    geschPersList = geschPersList.filter(
      geschPers => !modelValue.some(val => val.geschaedigtePerson?.correlationId === geschPers.correlationId)
    );
  }

  return [
    {
      id:         'geschaedigtePerson',
      type:       TYPE_HAFTPFLICHT_GESCHAEDIGTE_PERSON,
      value:      {
        id,
        vorhanden,
        geschaedigtePerson
      },
      additional: {
        geschPersList,
        updateClaim,
        paperProcessOverride,
        detailsNachbringen: aufstellungVorhanden === AufstellungVorhanden.Nachgebracht
      }
    },
    {
      id:        'spritzschaden_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Spritzschaden',
      hidden:    true,
      relations: !spritzschaden ? [] : [GESCHAEDIGTE_PERSON_RELATION],
      group:     [
        {
          id:            'spritzschaden_instructions',
          type:          TYPE_INFO,
          content:       'Wurde eine Bio-Kultur beschädigt?',
          isInstruction: true
        },
        {
          id:       'bioKultur',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          required: true,
          label:    'Spritzschaden',
          options:  [
            {
              label: 'Ja',
              value: true
            },
            {
              label: 'Nein',
              value: false
            }
          ],
          value:    bioKultur
        }
      ]
    },
    {
      id:        'aufstellung_group',
      type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
      label:     'Schadenaufstellung',
      hidden:    true,
      layout:    {
        element: {
          host: 'gsm-section-headline-3'
        }
      },
      relations: [GESCHAEDIGTE_PERSON_RELATION],
      group:     [
        {
          id:            'aufstellungVorhanden_instruction',
          type:          TYPE_INFO,
          content:       'Ist eine Schadenaufstellung vorhanden?',
          isInstruction: true
        },
        {
          id:       'aufstellungVorhanden',
          type:     DYNAMIC_FORM_CONTROL_TYPE_SELECT,
          label:    'Schadenaufstellung',
          required: true,
          options:  [
            {
              label: 'Ja, es ist eine Schadenaufstellung vorhanden.',
              value: AufstellungVorhanden.Vorhanden
            },
            {
              label: 'Ja, eine Schadenaufstellung wird nachgebracht.',
              value: AufstellungVorhanden.Nachgebracht
            },
            {
              label: 'Nein, Schadenaufstellung erstellen.',
              value: AufstellungVorhanden.Erfassen
            }
          ],
          value:    aufstellungVorhanden
        },
        {
          id:           'aufstellungFileList',
          type:         TYPE_CUSTOM_FILE_UPLOAD,
          labelTooltip: 'Dokument hinzufügen',
          required:     true,
          hidden:       true,
          relations:    [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                }
              ]
            }
          ],
          value:        aufstellungFileList
        },
        {
          id:        'spritzschadenAufstellung',
          type:      TYPE_HAFTPFLICHT_SPRITZ_WALDARBEITEN_AUFSTELLUNG,
          required:  true,
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          value:     spritzschadenAufstellung
        },
        {
          id:        'aufstellungZahlungsempfaenger_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                },
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          group:     [
            {
              id:            'aufstellungZahlungsempfaenger_instruction',
              type:          TYPE_INFO,
              content:       'Bitte wählen Sie den Zahlungsempfänger der Schadenaufstellung aus.',
              isInstruction: true
            },
            {
              id:                     'aufstellungZahlungsempfaenger',
              type:                   TYPE_ZAHLUNGSEMPFAENGER,
              required:               true,
              kunden:                 kundeList,
              removeRechnungssteller: true,
              addDirektAnGeschPerson: true,
              additional:             {
                immediateValidation: true
              },
              value:                  aufstellungZahlungsempfaenger
            }
          ]
        },
        {
          id:        'aufstellungBelege_group',
          type:      DYNAMIC_FORM_CONTROL_TYPE_GROUP,
          label:     'Belege zur Schadenaufstellung',
          hidden:    true,
          relations: [
            {
              match: MATCH_VISIBLE,
              when:  [
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Vorhanden
                },
                {
                  id:    'aufstellungVorhanden',
                  value: AufstellungVorhanden.Erfassen
                }
              ]
            }
          ],
          group:     [
            {
              id:         'aufstellungBelege',
              type:       TYPE_DOKUMENTEN_ANGABE,
              additional: {
                optionalTypes:       docTypBelege,
                kunden:              kundeList,
                immediateValidation: true
              },
              value:      belege
            }
          ]
        }
      ]
    }
  ];
}
